/**
 * @ooh-ecosystem/auth - Auth Context
 * 
 * React context for authentication state and actions.
 */

import { createContext } from 'react';
import type {
  AuthState,
  AuthUser,
  Tenant,
  UseAuthReturn,
  UsePermissionsReturn,
  UseClientViewReturn,
  ClientViewSession,
} from '../types';

// =============================================================================
// Context Value Type
// =============================================================================

export interface AuthContextValue {
  // Core state
  state: AuthState;
  
  // User data
  user: AuthUser | null;
  tenant: Tenant | null;
  
  // Auth actions
  login: UseAuthReturn['login'];
  logout: UseAuthReturn['logout'];
  switchTenant: UseAuthReturn['switchTenant'];
  refreshSession: UseAuthReturn['refreshSession'];
  
  // Permissions
  permissions: UsePermissionsReturn;
  
  // Client view (impersonation)
  clientView: UseClientViewReturn;
  
  // Internal - for Supabase client hook
  getAccessToken: () => string | null;
}

// =============================================================================
// Context Creation
// =============================================================================

export const AuthContext = createContext<AuthContextValue | null>(null);

AuthContext.displayName = 'AuthContext';

// =============================================================================
// Context Hook (internal)
// =============================================================================

export function useAuthContext(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext must be used within an AuthProvider. ' +
      'Make sure your component is wrapped in <AuthProvider>.'
    );
  }
  return context;
}

// Need to import React for useContext
import React from 'react';
